import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSubscriptionAccessStore } from '@/stores/subscriptionAccess'
import { APP_ROUTES } from '@/const/appRoutes'
import { PERMISSION_DENIED_MESSAGES } from '@/const/permissionDeniedCopy'

const MANAGEMENT_ROLES = ['GUIDE', 'MENTOR']

export function usePermissionDeniedView() {
  const router = useRouter()
  const userStore = useUserStore()
  const subscriptionAccessStore = useSubscriptionAccessStore()
  const { hasAccess, errorDetails, platformAccessDenied } = storeToRefs(
    subscriptionAccessStore
  )

  const isRoleChanged = ref(false)
  const accessSyncPending = ref(true)

  const isSubscriptionDenied = computed(() => !hasAccess.value)
  // platformAccessDenied is true only when hasAccess is true (set in syncPlatformAccessDenied)
  const isPlatformDenied = computed(() => platformAccessDenied.value)
  const showPaywallUi = computed(
    () => isSubscriptionDenied.value || isPlatformDenied.value
  )

  const pageTitle = computed(() => {
    if (isRoleChanged.value) return PERMISSION_DENIED_MESSAGES.roleChanged.title
    if (isPlatformDenied.value) return PERMISSION_DENIED_MESSAGES.platformDenied.title
    if (isSubscriptionDenied.value) return PERMISSION_DENIED_MESSAGES.subscriptionDenied.title
    return PERMISSION_DENIED_MESSAGES.rolePending.title
  })

  // Subscription denied: always prefer backend (message, action, action_url from API)
  const message = computed(() => {
    if (isPlatformDenied.value) {
      return PERMISSION_DENIED_MESSAGES.platformDenied.message
    }
    if (isSubscriptionDenied.value) {
      return (
        errorDetails.value?.message ||
        subscriptionAccessStore.message ||
        PERMISSION_DENIED_MESSAGES.subscriptionDenied.fallbackMessage
      )
    }
    return PERMISSION_DENIED_MESSAGES.rolePending.message
  })

  const buttonText = computed(() => {
    if (isRoleChanged.value) return PERMISSION_DENIED_MESSAGES.roleChanged.primaryAction
    if (isPlatformDenied.value) return PERMISSION_DENIED_MESSAGES.platformDenied.primaryAction
    if (isSubscriptionDenied.value) {
      return (
        errorDetails.value?.action ||
        subscriptionAccessStore.actionButtonText ||
        PERMISSION_DENIED_MESSAGES.subscriptionDenied.fallbackAction
      )
    }
    return PERMISSION_DENIED_MESSAGES.logout.primaryAction
  })

  const actionUrl = computed(() => {
    if (isPlatformDenied.value) {
      return PERMISSION_DENIED_MESSAGES.platformDenied.actionPath
    }
    if (showPaywallUi.value) {
      return (
        errorDetails.value?.actionUrl ||
        subscriptionAccessStore.actionUrl ||
        APP_ROUTES.TUITION
      )
    }
    return APP_ROUTES.TUITION
  })

  const handleLogout = async () => {
    await userStore.logout()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/login')
  }

  const handleAction = async () => {
    if (showPaywallUi.value) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push(actionUrl.value)
    } else {
      await handleLogout()
    }
  }

  const checkUserRoleAndRedirect = async () => {
    try {
      const roleCheck = await userStore.checkUserRole()
      const currentRole = (roleCheck.current_role || '').toUpperCase()

      if (roleCheck.role_changed) {
        isRoleChanged.value = true
        await userStore.logout()
        return
      }

      if (MANAGEMENT_ROLES.includes(currentRole)) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/manage')
      } else if (currentRole === 'FELLOW') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/dashboard')
      }
    } catch {
      if (hasAccess.value) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/')
      }
    }
  }

  onMounted(async () => {
    try {
      await subscriptionAccessStore.checkStatus(false, {
        preserveStateOnNetworkError: true
      })
      if (hasAccess.value && !platformAccessDenied.value) {
        await checkUserRoleAndRedirect()
      }
    } finally {
      accessSyncPending.value = false
    }
  })

  return {
    accessSyncPending,
    pageTitle,
    message,
    buttonText,
    showPaywallUi,
    handleAction,
    handleLogout
  }
}
